import { useState } from 'react';
import type { WbProduct } from '../hooks/useWbProducts';
import type { WbProductGroup } from '../hooks/useWbProductGroups';
import styles from './kioskProductBrowser.module.css';

interface KioskProductBrowserProps {
  products: WbProduct[];
  groups: WbProductGroup[];
  onSelect: (productEncId: string) => void;
  onClose: () => void;
}

const UNGROUPED = '__ungrouped__';

export function KioskProductBrowser({
  products,
  groups,
  onSelect,
  onClose,
}: KioskProductBrowserProps) {
  const [selectedGroup, setSelectedGroup] = useState<string | null>(null);

  // Active, non-excluded groups sorted by sort_order.
  const visibleGroups = groups
    .filter((g) => g.is_active && !g.is_excluded)
    .sort((a, b) => a.sort_order - b.sort_order);

  // products prop is already filtered to is_active: true by the parent.
  const productsForGroup = (code: string) =>
    products.filter((p) => p.group?.code === code);

  const ungroupedProducts = products.filter((p) => p.group === null);

  const groupsWithProducts = visibleGroups.filter(
    (g) => productsForGroup(g.code).length > 0,
  );

  if (selectedGroup !== null) {
    const isUngrouped = selectedGroup === UNGROUPED;
    const visibleProducts = isUngrouped
      ? ungroupedProducts
      : productsForGroup(selectedGroup);
    const heading = isUngrouped
      ? 'Ungrouped'
      : (visibleGroups.find((g) => g.code === selectedGroup)?.name ?? 'Products');

    return (
      <div className={styles.browser}>
        <div className={styles.browserHeader}>
          <button
            type="button"
            className={styles.backBtn}
            onClick={() => setSelectedGroup(null)}
          >
            ← Back to groups
          </button>
          <span className={styles.browserTitle}>{heading}</span>
          <button
            type="button"
            className={styles.closeBtn}
            onClick={onClose}
            aria-label="Close"
          >
            ✕
          </button>
        </div>
        <div className={styles.cardGrid}>
          {visibleProducts.map((p) => (
            <button
              key={p.enc_id}
              type="button"
              className={styles.productCard}
              onClick={() => {
                onSelect(p.enc_id);
                onClose();
              }}
            >
              <span className={styles.productName}>{p.name}</span>
              <span className={styles.productCode}>{p.code}</span>
              <span className={styles.modeBadge}>
                {p.mode === 'unit' ? 'Unit' : 'Weight'}
              </span>
            </button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className={styles.browser}>
      <div className={styles.browserHeader}>
        <span className={styles.browserTitle}>Browse Products</span>
        <button
          type="button"
          className={styles.closeBtn}
          onClick={onClose}
          aria-label="Close"
        >
          ✕
        </button>
      </div>
      <div className={styles.cardGrid}>
        {groupsWithProducts.map((g) => (
          <button
            key={g.enc_id}
            type="button"
            className={styles.groupCard}
            onClick={() => setSelectedGroup(g.code)}
          >
            <span className={styles.groupName}>{g.name}</span>
            <span className={styles.groupCode}>{g.code}</span>
            <span className={styles.countBadge}>{productsForGroup(g.code).length}</span>
          </button>
        ))}
        {ungroupedProducts.length > 0 && (
          <button
            type="button"
            className={styles.groupCard}
            onClick={() => setSelectedGroup(UNGROUPED)}
          >
            <span className={styles.groupName}>Ungrouped</span>
            <span className={styles.groupCode}>—</span>
            <span className={styles.countBadge}>{ungroupedProducts.length}</span>
          </button>
        )}
      </div>
    </div>
  );
}
